import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subscription } from 'rxjs';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import {
  Project,
  PullTestPiling,
  PullTestingModule,
  stripProjectModuleEmbeds,
} from 'shared_models';
import { AuthService } from '../auth.service';
import { LoggerService } from '../logger.service';
import { ProjectFacadeService } from '../project-facade.service';
import { PullTestingService } from '../pull-testing.service';
import { OfflinePullTestStoreService } from './offline-pull-test-store.service';
import {
  OfflineActiveSession,
  OfflinePendingListItem,
  OfflinePullTestSnapshot,
  offlineActionLabel,
} from './offline-pull-test.types';
import { OfflineExitError } from './offline-exit.error';
import { OfflineMutation } from './offline-pull-test.types';

const DOWNLOAD_PAGE_SIZE = 100;
const SYNC_MAX_RETRIES = 3;
const SYNC_RETRY_DELAY_MS = 1500;
/** Guards against a hung Cognito refresh (e.g. no cognito user on reload) blocking exit forever. */
const REFRESH_TIMEOUT_MS = 8000;
/** Per-mutation upload timeout so a stalled request cannot hang the whole exit flow. */
const UPLOAD_TIMEOUT_MS = 15000;

export interface OfflineDownloadState {
  active: boolean;
  downloaded: number;
  total: number;
  error: string | null;
}

export interface OfflineUploadState {
  active: boolean;
  uploaded: number;
  total: number;
  error: string | null;
}

export interface OfflineRehydrateResult {
  restored: boolean;
  projectId?: string;
}

@Injectable({ providedIn: 'root' })
export class OfflineModeService {
  private activeSession: OfflineActiveSession | null = null;
  private snapshot: OfflinePullTestSnapshot | null = null;
  /** True when this app session restored offline mode from Ionic Storage (crash/restart). */
  private rehydratedFromStorage = false;
  /** Set once the welcome-back prompt is confirmed or dismissed for this rehydrated session. */
  private rehydrationPromptHandled = false;
  /** Last logged-in user id for which {@link rehydrate} completed. */
  private rehydrateCacheUserId: string | null | undefined = undefined;
  private rehydrateInFlight: Promise<OfflineRehydrateResult> | null = null;

  private readonly active$ = new BehaviorSubject<boolean>(false);
  private readonly pendingCount$ = new BehaviorSubject<number>(0);
  private readonly pendingItems$ = new BehaviorSubject<OfflinePendingListItem[]>([]);
  private readonly downloadState$ = new BehaviorSubject<OfflineDownloadState>({
    active: false,
    downloaded: 0,
    total: 0,
    error: null,
  });
  private readonly uploadState$ = new BehaviorSubject<OfflineUploadState>({
    active: false,
    uploaded: 0,
    total: 0,
    error: null,
  });

  readonly isActive$ = this.active$.asObservable();
  readonly pendingCountObservable$ = this.pendingCount$.asObservable();
  readonly pendingItemsObservable$ = this.pendingItems$.asObservable();
  readonly downloadStateObservable$ = this.downloadState$.asObservable();
  readonly uploadStateObservable$ = this.uploadState$.asObservable();

  constructor(
    private readonly store: OfflinePullTestStoreService,
    private readonly authService: AuthService,
    private readonly pullTestingService: PullTestingService,
    private readonly projectFacade: ProjectFacadeService,
    private readonly logger: LoggerService
  ) {}

  /**
   * Restores offline mode from Ionic Storage when the stored session belongs to the current user.
   * Another user's offline data remains on device but is not activated for this login.
   */
  async rehydrate(): Promise<OfflineRehydrateResult> {
    const session = await this.store.getActiveSession();
    console.log('[OfflineMode] rehydrate: stored session =', session);
    if (!session) {
      this.clearInMemoryState();
      return { restored: false };
    }

    const currentUserId = await this.resolveCurrentUserId();
    if (!currentUserId || currentUserId !== session.userId) {
      console.log('[OfflineMode] rehydrate: session user mismatch — skipping for this login', {
        storedUserId: session.userId,
        currentUserId,
      });
      this.clearInMemoryState();
      return { restored: false };
    }

    const snap = await this.store.getSnapshot(session.userId, session.projectId);
    if (!snap) {
      console.warn('[OfflineMode] rehydrate: session found but no snapshot — clearing session');
      await this.store.setActiveSession(null);
      this.clearInMemoryState();
      return { restored: false };
    }

    this.activeSession = session;
    this.snapshot = snap;
    this.rehydratedFromStorage = true;
    this.rehydrationPromptHandled = false;
    this.active$.next(true);
    this.authService.pauseForOfflineMode();
    await this.refreshPendingState();
    console.log('[OfflineMode] rehydrate: restored offline mode for project', session.projectId);
    return { restored: true, projectId: session.projectId };
  }

  /**
   * Ensures {@link rehydrate} has run for the current user (deduped). Guards and app shell should
   * await this before checking {@link isActive} so user-id gating is applied first.
   */
  async ensureRehydrated(): Promise<OfflineRehydrateResult> {
    const currentUserId = await this.resolveCurrentUserId();

    if (this.activeSession && currentUserId !== this.activeSession.userId) {
      console.log('[OfflineMode] ensureRehydrated: in-memory session user mismatch — clearing');
      this.clearInMemoryState();
    }

    if (
      this.rehydrateCacheUserId === currentUserId &&
      currentUserId !== null &&
      !this.rehydrateInFlight
    ) {
      return {
        restored: this.isActive(),
        projectId: this.activeSession?.projectId,
      };
    }

    if (this.rehydrateInFlight) {
      return this.rehydrateInFlight;
    }

    this.rehydrateInFlight = this.rehydrate()
      .then(result => {
        this.rehydrateCacheUserId = currentUserId;
        return result;
      })
      .finally(() => {
        this.rehydrateInFlight = null;
      });

    return this.rehydrateInFlight;
  }

  /** Clears the rehydrate cache so the next login re-evaluates stored offline state. */
  invalidateRehydrateCache(): void {
    this.rehydrateCacheUserId = undefined;
  }

  private async resolveCurrentUserId(): Promise<string | null> {
    try {
      const user = await firstValueFrom(this.authService.getCurrentUser());
      return user?.userID?.trim() ?? null;
    } catch {
      return null;
    }
  }

  isActive(): boolean {
    return this.active$.value;
  }

  isActiveForProject(projectId: string | null | undefined): boolean {
    if (!projectId || !this.activeSession) {
      return false;
    }
    return this.activeSession.projectId === projectId;
  }

  /** Whether offline mode was restored from device storage this app session (not a fresh enter). */
  wasRehydratedFromStorage(): boolean {
    return this.rehydratedFromStorage;
  }

  /** Whether the welcome-back prompt should still be offered for a rehydrated session. */
  shouldOfferRehydrationPrompt(): boolean {
    return this.rehydratedFromStorage && !this.rehydrationPromptHandled && this.isActive();
  }

  markRehydrationPromptHandled(): void {
    this.rehydrationPromptHandled = true;
  }

  getActiveSession(): OfflineActiveSession | null {
    return this.activeSession;
  }

  getSnapshot(): OfflinePullTestSnapshot | null {
    return this.snapshot;
  }

  setSnapshotInMemory(snapshot: OfflinePullTestSnapshot): void {
    this.snapshot = snapshot;
  }

  getPendingCount(): number {
    return this.pendingCount$.value;
  }

  getPendingItems(): OfflinePendingListItem[] {
    return this.pendingItems$.value;
  }

  async refreshPendingState(): Promise<void> {
    if (!this.activeSession) {
      this.pendingCount$.next(0);
      this.pendingItems$.next([]);
      return;
    }

    const queue = await this.store.getQueue(
      this.activeSession.userId,
      this.activeSession.projectId
    );
    const pending = queue.filter(m => m.status === 'pending' || m.status === 'failed');
    this.pendingCount$.next(pending.length);
    this.pendingItems$.next(
      pending.map(m => ({
        displayPilingId: m.displayPilingId,
        actionLabel: offlineActionLabel(m.action),
        createdAt: m.createdAt,
      }))
    );
  }

  async isOnline(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return typeof navigator !== 'undefined' ? navigator.onLine : true;
    }
    const status = await Network.getStatus();
    return status.connected;
  }

  async enterOfflineMode(projectId: string): Promise<void> {
    if (!(await this.isOnline())) {
      throw new Error('An internet connection is required to download project data.');
    }

    if (this.isActive()) {
      throw new Error('Offline mode is already active.');
    }

    const user = await firstValueFrom(this.authService.getCurrentUser());
    const userId = user?.userID?.trim();
    if (!userId) {
      throw new Error('Could not determine the current user.');
    }

    await this.downloadProjectData(userId, projectId);

    const downloadedAt = new Date().toISOString();
    this.activeSession = { userId, projectId, downloadedAt };
    await this.store.setActiveSession(this.activeSession);
    await this.store.setMeta(userId, projectId, {
      downloadedAt,
      totalExpected: this.snapshot?.pilings.length ?? 0,
      downloadComplete: true,
    });

    this.active$.next(true);
    this.authService.pauseForOfflineMode();
    await this.refreshPendingState();
  }

  async exitOfflineMode(): Promise<void> {
    console.log('[OfflineMode] exitOfflineMode: start', {
      active: this.isActive(),
      session: this.activeSession,
    });
    if (!this.isActive() || !this.activeSession) {
      console.log('[OfflineMode] exitOfflineMode: not active — nothing to do');
      return;
    }

    const pendingTotal = this.pendingCount$.value;
    this.uploadState$.next({ active: true, uploaded: 0, total: pendingTotal, error: null });

    try {
      const online = await this.isOnline();
      console.log('[OfflineMode] exitOfflineMode: online =', online);
      if (!online) {
        throw new OfflineExitError(
          'An internet connection is required to upload your offline changes and switch back online.',
          'network'
        );
      }

      console.log('[OfflineMode] exitOfflineMode: refreshing login session…');
      let refreshed = false;
      try {
        refreshed = await this.withTimeout(
          this.authService.tryRefreshSession(),
          REFRESH_TIMEOUT_MS,
          'refresh login session'
        );
      } catch (e) {
        console.error('[OfflineMode] exitOfflineMode: session refresh errored/timed out', e);
        throw new OfflineExitError(
          'We could not refresh your login session (it may have timed out). Enter your password below to sign in and upload your offline changes.',
          'session'
        );
      }
      console.log('[OfflineMode] exitOfflineMode: refreshed =', refreshed);
      if (!refreshed) {
        throw new OfflineExitError(
          'Your login session could not be refreshed. Enter your password below to sign in and upload your offline changes.',
          'session'
        );
      }

      console.log('[OfflineMode] exitOfflineMode: syncing pending mutations…');
      let syncOk = false;
      try {
        syncOk = await this.syncPendingMutations();
      } catch (e) {
        console.error('[OfflineMode] exitOfflineMode: sync errored', e);
        throw new OfflineExitError(
          'Uploading your offline changes failed. Try again when your connection is stable.',
          'sync'
        );
      }
      console.log('[OfflineMode] exitOfflineMode: syncOk =', syncOk);
      if (!syncOk) {
        throw new OfflineExitError(
          'Some offline changes could not be uploaded. Try again when your connection is stable.',
          'sync'
        );
      }

      const { userId, projectId } = this.activeSession;
      await this.store.clearAllProjectData(userId, projectId);
      await this.store.setActiveSession(null);

      this.clearInMemoryState();
      console.log('[OfflineMode] exitOfflineMode: complete — back online');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to leave offline mode.';
      this.uploadState$.next({ ...this.uploadState$.value, active: false, error: msg });
      if (e instanceof OfflineExitError) {
        throw e;
      }
      throw new OfflineExitError(msg, 'sync');
    }
  }

  async ensureSnapshotLoaded(projectId: string): Promise<void> {
    if (this.snapshot && this.snapshot.project.projectID === projectId) {
      return;
    }
    if (!this.activeSession) {
      return;
    }
    const snap = await this.store.getSnapshot(this.activeSession.userId, projectId);
    if (snap) {
      this.snapshot = snap;
    }
  }

  private async downloadProjectData(userId: string, projectId: string): Promise<void> {
    this.downloadState$.next({ active: true, downloaded: 0, total: 0, error: null });

    try {
      const statusRes = await firstValueFrom(this.pullTestingService.getModuleStatus(projectId));
      const totalExpected = statusRes?.data?.totalPilings ?? 0;

      await this.projectFacade.loadProjectModules(projectId, { force: true });
      const facadeSnap = this.projectFacade.getSnapshot();
      if (facadeSnap.phase === 'error' || !facadeSnap.project || !facadeSnap.pullTestingModule) {
        throw new Error(facadeSnap.errorMessage ?? 'Failed to load project data.');
      }

      const project = stripProjectModuleEmbeds(facadeSnap.project) as Project;
      const pullTestingModule = facadeSnap.pullTestingModule as PullTestingModule;

      let existingProgress = await this.store.getDownloadProgress(userId, projectId);
      let pilings: PullTestPiling[] = existingProgress?.pilings ?? [];
      let startKey: string | undefined = existingProgress?.nextKey ?? undefined;

      this.downloadState$.next({
        active: true,
        downloaded: pilings.length,
        total: totalExpected || pilings.length,
        error: null,
      });

      let nextKey: string | null = startKey ?? null;
      do {
        const res = await firstValueFrom(
          this.pullTestingService.listPullTestPilings(projectId, {
            limit: DOWNLOAD_PAGE_SIZE,
            startKey: nextKey ?? undefined,
          })
        );
        const batch = (res?.data ?? []) as PullTestPiling[];
        pilings = this.store.mergePilings(pilings, batch);

        const nk = res?.nextKey;
        nextKey = nk && String(nk).length > 0 ? String(nk) : null;

        await this.store.setDownloadProgress(userId, projectId, { pilings, nextKey });

        this.downloadState$.next({
          active: true,
          downloaded: pilings.length,
          total: totalExpected || pilings.length,
          error: null,
        });
      } while (nextKey);

      this.snapshot = { project, pullTestingModule, pilings };
      await this.store.setSnapshot(userId, projectId, this.snapshot);
      await this.store.clearDownloadProgress(userId, projectId);

      this.downloadState$.next({
        active: false,
        downloaded: pilings.length,
        total: totalExpected || pilings.length,
        error: null,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Download failed.';
      this.downloadState$.next({
        ...this.downloadState$.value,
        active: false,
        error: msg,
      });
      throw e;
    }
  }

  async retryDownload(projectId: string): Promise<void> {
    if (!(await this.isOnline())) {
      throw new Error('An internet connection is required to retry the download.');
    }
    const user = await firstValueFrom(this.authService.getCurrentUser());
    const userId = user?.userID?.trim();
    if (!userId) {
      throw new Error('Could not determine the current user.');
    }
    await this.downloadProjectData(userId, projectId);
  }

  private async syncPendingMutations(): Promise<boolean> {
    /**
     * Sync policy (product): the API should accept operator changes even when they overlap
     * with concurrent server edits (last-write-wins on apply). Discrepancies are recorded for
     * project-manager review in the Pull Testing event log — not blocked at upload time.
     * See docs/pull-testing/offline-mode.md.
     */
    if (!this.activeSession) {
      return true;
    }

    const { userId, projectId } = this.activeSession;
    const queue = await this.store.getQueue(userId, projectId);
    const pending = queue.filter(m => m.status !== 'uploaded');
    const total = pending.length;
    this.uploadState$.next({ active: true, uploaded: 0, total, error: null });
    if (!pending.length) {
      return true;
    }

    const sorted = [...pending].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    let uploaded = 0;
    for (const mutation of sorted) {
      mutation.status = 'uploading';
      await this.store.setQueue(userId, projectId, queue);

      let success = false;
      for (let attempt = 0; attempt < SYNC_MAX_RETRIES; attempt++) {
        try {
          await this.uploadMutationWithTimeout(projectId, mutation);
          mutation.status = 'uploaded';
          success = true;
          break;
        } catch (e) {
          this.logger.error('Offline sync mutation failed', e);
          if (attempt < SYNC_MAX_RETRIES - 1) {
            await this.delay(SYNC_RETRY_DELAY_MS * (attempt + 1));
          }
        }
      }

      if (!success) {
        mutation.status = 'failed';
        await this.store.setQueue(userId, projectId, queue);
        await this.refreshPendingState();
        this.uploadState$.next({
          active: false,
          uploaded,
          total,
          error: `Upload failed after ${uploaded} of ${total} changes.`,
        });
        return false;
      }

      uploaded++;
      this.uploadState$.next({ active: true, uploaded, total, error: null });
      await this.store.setQueue(userId, projectId, queue);
    }

    await this.refreshPendingState();
    this.uploadState$.next({ active: false, uploaded, total, error: null });
    return true;
  }

  private uploadMutationWithTimeout(projectId: string, mutation: OfflineMutation): Promise<void> {
    const label = `upload ${mutation.action} for ${mutation.pullTestPilingID}`;
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let subscription: Subscription | null = null;
      const timer = setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        subscription?.unsubscribe();
        console.error(`[OfflineMode] uploadMutationWithTimeout: "${label}" timed out after ${UPLOAD_TIMEOUT_MS}ms`);
        reject(new Error(`Timed out: ${label}`));
      }, UPLOAD_TIMEOUT_MS);

      subscription = this.uploadMutationObservable(projectId, mutation).subscribe({
        next: () => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timer);
          subscription?.unsubscribe();
          resolve();
        },
        error: err => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timer);
          subscription?.unsubscribe();
          reject(err);
        },
      });
    });
  }

  private uploadMutationObservable(projectId: string, mutation: OfflineMutation): Observable<unknown> {
    switch (mutation.action) {
      case 'installed':
      case 'tensile_tested':
      case 'lateral_tested':
      case 'compression_tested':
        return this.pullTestingService.patchPullTestPiling(
          projectId,
          mutation.pullTestPilingID,
          mutation.payload,
          { clientMutationId: mutation.clientMutationId }
        );
      case 'soak_bypassed': {
        const reason =
          typeof mutation.payload['reason'] === 'string' ? mutation.payload['reason'] : '';
        return this.pullTestingService.soakBypass(projectId, mutation.pullTestPilingID, reason, {
          clientMutationId: mutation.clientMutationId,
        });
      }
      case 'project_action':
        return this.pullTestingService.recordPullTestingProjectAction(projectId, mutation.payload, {
          clientMutationId: mutation.clientMutationId,
        });
      default:
        throw new Error(`Unknown offline mutation action: ${mutation.action}`);
    }
  }

  private clearInMemoryState(): void {
    const resumeAuth = this.authService.isOfflineModeSuppressingExpiry();
    this.activeSession = null;
    this.snapshot = null;
    this.rehydratedFromStorage = false;
    this.rehydrationPromptHandled = false;
    this.active$.next(false);
    this.pendingCount$.next(0);
    this.pendingItems$.next([]);
    this.downloadState$.next({ active: false, downloaded: 0, total: 0, error: null });
    this.uploadState$.next({ active: false, uploaded: 0, total: 0, error: null });
    this.invalidateRehydrateCache();
    if (resumeAuth) {
      this.authService.resumeAfterOfflineMode();
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /** Rejects with a labeled error if `p` does not settle within `ms`, so callers never hang. */
  private withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        console.error(`[OfflineMode] withTimeout: "${label}" timed out after ${ms}ms`);
        reject(new Error(`Timed out: ${label}`));
      }, ms);
      p.then(
        value => {
          clearTimeout(timer);
          resolve(value);
        },
        err => {
          clearTimeout(timer);
          reject(err);
        }
      );
    });
  }
}
